import { lazy } from "react";
import {
  createBrowserRouter,
  createRoutesFromElements,
  Route,
} from "react-router-dom";
// =================================================================================
//  Layout
// =================================================================================
import BackgroundLayout from "../layouts/BackgroundLayout";
import AdminLayout from "@/layouts/AdminBackgroundLayout/AdminBackgroundLayout";
import AdminAuthLayout from "@/layouts/AdminAuthLayout/AdminAuthLayout";
import TeacherLayout from "@/layouts/TeacherLayout/TeacherLayout";
import { ProtectedRoutes, RoleGuard } from "@/guards";
import { RequirePaid } from "@/guards/RequirePaid";
import { RequireUnpaid } from "@/guards/RequireUnpaid";
// =================================================================================
//  Web Pages
// =================================================================================
// const IndexPage = lazy(() => import("../pages/Web/FrontFacing/FrontFacing"));
const WebLoginPage = lazy(() => import("../pages/Web/Login/Login"));
const WebReferralCodePage = lazy(
  () => import("../pages/Web/ReferralCode/ReferralCode"),
);
const WebSalesAndPromotionsPage = lazy(
  () => import("../pages/Web/SalesAndPromotions/SalesAndPromotions"),
);
const WebPayentGatewayPage = lazy(
  () => import("../pages/Web/PaymentGateways/PaymentGateways"),
);
const WebVerifyLinkPage = lazy(
  () => import("../pages/Web/VerifyLink/VerifyLink"),
);
const WebCreditCardPage = lazy(
  () => import("../pages/Web/CreditCard/CreditCard"),
);
const WebElanPayPage = lazy(() => import("../pages/Web/ElanPay/ElanPay"));
const ElanPayPaymentSuccess = lazy(
  () => import("../pages/Web/ElanPayPaymentSuccess/ElanPayPaymentSuccess"),
);
const WebCoursesPage = lazy(() => import("../pages/Web/Courses/Courses"));
const TeacherWidrawalInvoicePage = lazy(
  () => import("../pages/Web/TeacherInvoiceWithdrawl/TeacherInvoiceWithdrawl"),
);
const TeacherWithdrawInvoiceSuccessPage = lazy(
  () =>
    import("../pages/Web/TeacherInvoiceWithdrawalSuccess/TeacherInvoiceWithdrawalSuccess"),
);
const TestingPage = lazy(
  () => import("../pages/Web/ResponseTesting/ResponseTesting"),
);

// =================================================================================
//  Admin Dashboard Pages
// =================================================================================
const IndexPageDashboard = lazy(
  () => import("../pages/Dashboard/Index/Dashboard"),
);
const AdminLoginPage = lazy(
  () => import("../pages/Dashboard/auth/Login/Login"),
);
const AdminYubiKeyVerification = lazy(
  () =>
    import("../pages/Dashboard/auth/YubiKeyVerification/YubiKeyVerification"),
);
const AdminProfilePage = lazy(
  () => import("../pages/Dashboard/AdminProfile/AdminProfile"),
);
const FileManagerPage = lazy(
  () => import("../pages/Dashboard/FileManager/FileManager"),
);
const UserProfilePage = lazy(
  () => import("../pages/Dashboard/UserProfile/UserProfile"),
);
const TeacherProfilePage = lazy(
  () => import("../pages/Dashboard/TeacherProfile/TeacherProfile"),
);
const PaymentHistoryPage = lazy(
  () => import("../pages/Dashboard/PaymentHistory/PaymentHistory"),
);
const PayoutsPage = lazy(() => import("../pages/Dashboard/Payouts/Payouts"));
const RefferalsPage = lazy(
  () => import("../pages/Dashboard/Refferals/Refferals"),
);
const AdminReferralPage = lazy(
  () => import("../pages/Dashboard/Refferals/MyReferrals"),
);
const SalesAndPromotionsPage = lazy(
  () => import("../pages/Dashboard/SalesAndPromotions/SalesAndPromotions"),
);
const UsersPage = lazy(() => import("../pages/Dashboard/Users/Users"));
const AdminDashboardNotFoundPage = lazy(
  () => import("../pages/Dashboard/NotFound/NotFound"),
);
const NotificationPage = lazy(
  () => import("../pages/Dashboard/Notifications/Notifications"),
);

export const router = createBrowserRouter(
  createRoutesFromElements(
    <>
      {/* <Route path="/" element={<IndexPage />} /> */}
      {/** Public Routes */}
      <Route element={<BackgroundLayout />}>
        <Route path="/login" element={<WebLoginPage />} />
        // ✅ sob.com/refeeew123
        <Route path="/:referralCode" element={<WebReferralCodePage />} />
        // ✅ sob.com/p/SAVE20
        <Route path="/p/:promoCode" element={<WebSalesAndPromotionsPage />} />
        <Route
          path="/login/verifyToken/:token"
          element={<WebVerifyLinkPage />}
        />
        <Route
          path="/invoice/withdraw/success/:payoutId/:userId"
          element={<TeacherWithdrawInvoiceSuccessPage />}
        />
        <Route path="/testing" element={<TestingPage />} />
      </Route>
      {/* These routes require logged in */}
      <Route element={<BackgroundLayout />}>
        <Route element={<ProtectedRoutes />}>
          {/* <Route path="/courses" element={<WebCoursesPage />} />
          <Route path="/payment/methods" element={<WebPayentGatewayPage />} />
          <Route
            path="/payment/methods/credit-card"
            element={<WebCreditCardPage />}
          />
          <Route path="/payment/methods/btc" element={<WebElanPayPage />} />
          <Route
            path="/payment/methods/btc/success/:invoiceId"
            element={<ElanPayPaymentSuccess />}
          />*/}
          {/* Paid users only */}
          <Route element={<RequirePaid />}>
            <Route path="/courses" element={<WebCoursesPage />} />
          </Route>

          {/* Unpaid users only — the funnel, free back/forward movement */}
          <Route element={<RequireUnpaid />}>
            <Route path="/payment/methods" element={<WebPayentGatewayPage />} />
            <Route
              path="/payment/methods/credit-card"
              element={<WebCreditCardPage />}
            />
            <Route path="/payment/methods/btc" element={<WebElanPayPage />} />
          </Route>

          {/* Transition page — deliberately under NEITHER guard */}
          <Route
            path="/payment/methods/btc/success/:invoiceId"
            element={<ElanPayPaymentSuccess />}
          />
        </Route>
      </Route>

      {/* Teacher Routes */}
      <Route path="/" element={<TeacherLayout />}>
        <Route
          path="/payout/:payoutId/:token"
          element={<TeacherWidrawalInvoicePage />}
        />
        <Route path="/courses/portal" element={<WebCoursesPage />} />
      </Route>
      {/* Admin Auth Routes */}
      <Route path="admin" element={<AdminAuthLayout />}>
        <Route index path="login" element={<AdminLoginPage />} />
        {/* if sess */}
        <Route element={<ProtectedRoutes />}>
          <Route
            path="verifyTwoFactorAuth"
            element={<AdminYubiKeyVerification />}
          />
        </Route>
      </Route>
      {/* Admin Routes */}
      <Route path="dashboard" element={<AdminLayout />}>
        <Route element={<ProtectedRoutes />}>
          <Route element={<RoleGuard role="super_admin" />}>
            <Route index element={<IndexPageDashboard />} />
            <Route path="profile/admin" element={<AdminProfilePage />} />
            <Route path="profile/user/:id" element={<UserProfilePage />} />
            <Route
              path="profile/teacher/:id"
              element={<TeacherProfilePage />}
            />
            <Route path="users" element={<UsersPage />} />
            <Route path="file-manager" element={<FileManagerPage />} />
            <Route path="refferals/admin" element={<AdminReferralPage />} />
            <Route path="refferals" element={<RefferalsPage />} />

            <Route
              path="sales-and-promotions"
              element={<SalesAndPromotionsPage />}
            />
            <Route path="payment-history" element={<PaymentHistoryPage />} />
            <Route path="payouts" element={<PayoutsPage />} />
            <Route path="notification" element={<NotificationPage />} />
          </Route>
        </Route>
      </Route>
      <Route path="dashboard/*" element={<AdminDashboardNotFoundPage />} />
      <Route
        path="notFound"
        element={<AdminDashboardNotFoundPage isRestrictedAccess={true} />}
      />
      <Route
        path="*"
        element={<AdminDashboardNotFoundPage isRestrictedAccess={true} />}
      />
    </>,
  ),
);

// const AppRouter: React.FC = () => {
//   return <RouterProvider router={router} />;
// };
// export default AppRouter;
